// Word-level matching between two wordings of the same voice-over text.
//
// Two takes of a point are rarely voiced from exactly the same text: the prose
// gets a typo fixed, a stress mark added, a phrase re-voiced on its own. To
// find "the same words" in both, each text is reduced to a list of bare words
// (no case, no stress marks, no punctuation, no "Speaker N:" or [notes]) and
// the two lists are matched as a longest common subsequence.
//
// CommonJS on purpose: the same code runs in the browser (src/utils/wordMap.js
// re-exports it) and in the server-side task runner (tasks/), so a fragment
// is placed by one algorithm wherever the splice happens.

// Chrome the TTS reads as instructions, never as words.
const CHROME_RE = /^[ \t]*Speaker\s*\d+\s*:[ \t]*|\[[^\]\n]*\]/gm;

// A turn label plus the [notes] that directly follow it, possibly on the
// next line: "Speaker 2: [firmly]" or "Speaker 2:\n[warmly] [slowly]".
const TURN_RE = /^[ \t]*(Speaker\s*\d+\s*:)((?:\s*\[[^\]\n]*\])*)/gm;

// The bare form the server searches and the glossary keys by: case and stress
// marks dropped, nothing else. «ё» is NOT folded into «е» here — the glossary
// matches word forms literally, and the two are different words.
const bareOf = (s) => String(s)
  .replace(/́/g, '')
  .toLowerCase()
  .replace(/[^\p{L}\p{N}-]+/gu, '');

// A token as a word: the punctuation and the HTML glued to it taken off, a
// trailing stress mark kept (it is part of the spelling). Mirrors what the
// server does when it counts the words of a text.
const stripTags = (s) => s.replace(/^(?:<[^>]*>)+/, '').replace(/(?:<[^>]*>)+$/, '');
function wordFace(token) {
  let s = stripTags(String(token || '').trim());
  s = s.replace(/^[^\p{L}\p{N}<]+/u, '').replace(/[^\p{L}\p{N}́>]+$/u, '');
  return stripTags(s);
}

const normWord = (s) => s
  .replace(/́/g, '')
  .toLowerCase()
  .replace(/ё/g, 'е')
  .replace(/[^\p{L}\p{N}]+/gu, '');

// Words of a plain text: [{a, b, n}] — a/b is the character span (with any
// punctuation glued to the word), n the bare form used for matching.
function textWords(text) {
  const masked = text.replace(CHROME_RE, (m) => ' '.repeat(m.length));
  const out = [];
  const re = /\S+/g;
  let m;
  while ((m = re.exec(masked)) !== null) {
    const n = normWord(m[0]);
    if (n) out.push({ a: m.index, b: m.index + m[0].length, n });
  }
  return out;
}

// Bare forms of aligned words ({a, b} spans into `text`).
const alignedForms = (text, words) => words.map((w) => normWord(text.slice(w.a, w.b)));

// For each index of `a`, the index of the same word in `b`, or -1. Most edits
// touch a few words, so the common head and tail are matched directly and the
// quadratic table only covers what is left in between.
function matchWords(a, b) {
  const n = a.length;
  const m = b.length;
  const map = new Array(n).fill(-1);
  let h = 0;
  while (h < n && h < m && a[h] === b[h]) { map[h] = h; h += 1; }
  let t = 0;
  while (t < n - h && t < m - h && a[n - 1 - t] === b[m - 1 - t]) {
    map[n - 1 - t] = m - 1 - t;
    t += 1;
  }
  const A = a.slice(h, n - t);
  const B = b.slice(h, m - t);
  const N = A.length;
  const M = B.length;
  if (!N || !M) return map;
  const W = M + 1;
  const dp = new Uint32Array((N + 1) * W);
  for (let i = N - 1; i >= 0; i -= 1) {
    for (let j = M - 1; j >= 0; j -= 1) {
      dp[i * W + j] = A[i] === B[j]
        ? dp[(i + 1) * W + j + 1] + 1
        : Math.max(dp[(i + 1) * W + j], dp[i * W + j + 1]);
    }
  }
  let i = 0;
  let j = 0;
  while (i < N && j < M) {
    if (A[i] === B[j]) { map[h + i] = h + j; i += 1; j += 1; }
    else if (dp[(i + 1) * W + j] >= dp[i * W + j + 1]) i += 1;
    else j += 1;
  }
  return map;
}

// Carry the run of words [i0, i1] over to the other text. A matched end maps
// directly; an end whose word was edited snaps to the edge of its matched
// neighbour, so a changed word inside the run comes along as whatever
// replaced it. null when the whole run is gone from the other text.
function mapRange(map, i0, i1, targetLen) {
  let lo;
  let hi;
  if (map[i0] >= 0) lo = map[i0];
  else {
    let k = i0 - 1;
    while (k >= 0 && map[k] < 0) k -= 1;
    lo = k >= 0 ? map[k] + 1 : 0;
  }
  if (map[i1] >= 0) hi = map[i1];
  else {
    let k = i1 + 1;
    while (k < map.length && map[k] < 0) k += 1;
    hi = k < map.length ? map[k] - 1 : targetLen - 1;
  }
  return hi >= lo ? [lo, hi] : null;
}

// The speaker label and the [notes] right after it for the turn that `pos`
// falls in, normalised to one line: "Speaker 2: [firmly] ". Empty when the
// text has no labels before `pos`.
function turnPrefix(text, pos) {
  TURN_RE.lastIndex = 0;
  let last = null;
  let m;
  while ((m = TURN_RE.exec(text)) !== null && m.index < pos) last = m;
  if (!last) return '';
  const label = last[1].replace(/\s+/g, ' ');
  const tags = last[2].trim().replace(/\s+/g, ' ');
  return `${label} ${tags ? `${tags} ` : ''}`;
}

// The speaker a turn prefix names ("Speaker 2: [firmly] " -> 2), or null
// when the text carries no labels.
function speakerOfPrefix(prefix) {
  const m = /Speaker\s*(\d+)\s*:/.exec(String(prefix || ''));
  return m ? Number(m[1]) : null;
}

// The same fragment plan read by speaker `n`: the label in the prefix is
// swapped, the notes stay. A text with no labels gets a whole turn opening
// put in front — label plus the [firmly] delivery tag, the way the voice
// split writes every turn (server.js applyVoiceCode). With `n` null, or
// already the prefix's own speaker, the plan comes back as is.
function withSpeaker(frag, n) {
  if (!frag || !n || speakerOfPrefix(frag.prefix) === n) return frag;
  const prefix = speakerOfPrefix(frag.prefix) != null
    ? frag.prefix.replace(/Speaker\s*\d+\s*:/, `Speaker ${n}:`)
    : `Speaker ${n}: [firmly] ${frag.prefix}`;
  return { ...frag, prefix, text: prefix + frag.body, speaker: n };
}

// What "voice selection only" sends: the words picked in the prose, taken
// from the point's CURRENT text (the prose on screen may be the older wording
// the take was aligned against), with the turn's label and notes in front.
//   shownText/shownWords — the aligned text and its words; wi0..wi1 — the pick.
// -> {start, end, body, prefix, text, words} or null if the words are gone.
function fragmentFromSelection({ shownText, shownWords, wi0, wi1, currentText }) {
  if (!currentText) return null;
  const cur = textWords(currentText);
  if (!cur.length) return null;
  const map = matchWords(alignedForms(shownText, shownWords), cur.map((w) => w.n));
  const r = mapRange(map, wi0, wi1, cur.length);
  if (!r) return null;
  const start = cur[r[0]].a;
  const end = cur[r[1]].b;
  const body = currentText.slice(start, end);
  const prefix = turnPrefix(currentText, start);
  return { start, end, body, prefix, text: prefix + body, words: r[1] - r[0] + 1 };
}

// The single word a one-word selection points at, as the point's CURRENT text
// spells it — the take on screen may have been voiced from an older wording.
// -> {spelling, bare} or null when the word is no longer in the text.
function wordAtSelection({ shownText, shownWords, wi, currentText }) {
  const frag = fragmentFromSelection({ shownText, shownWords, wi0: wi, wi1: wi, currentText });
  if (!frag) return null;
  const spelling = wordFace(frag.body);
  const bare = bareOf(spelling);
  return bare ? { spelling, bare } : null;
}

// How the words of the take being edited line up with a donor that voices
// only a fragment. `donor` = {text, words, fragment: {parentText, start, end}}.
// -> {toSource, lo, hi, changed}: toSource[wi] is the donor word for word wi
// of the edited take (or -1); lo..hi the span of matched words; `changed`
// when the text moved on since the fragment was voiced and some of its words
// no longer have a partner.
function fragmentDonorMap(align, donor) {
  const { parentText, start, end } = donor.fragment;
  const P = textWords(parentText || '');
  const R = [];
  P.forEach((w, i) => { if (w.a >= start && w.b <= end) R.push(i); });
  const toSource = new Array(align.words.length).fill(-1);
  const parentToEdit = matchWords(P.map((w) => w.n), alignedForms(align.text, align.words));
  const rangeToDonor = matchWords(R.map((i) => P[i].n), alignedForms(donor.text, donor.words));
  let lo = -1;
  let hi = -1;
  let missing = 0;
  R.forEach((p, k) => {
    const wi = parentToEdit[p];
    if (wi < 0 || rangeToDonor[k] < 0) { missing += 1; return; }
    toSource[wi] = rangeToDonor[k];
    if (lo < 0) lo = wi;
    hi = wi;
  });
  if (lo >= 0) {
    for (let wi = lo; wi <= hi; wi += 1) if (toSource[wi] < 0) missing += 1;
  }
  return { toSource, lo, hi, changed: missing > 0 || lo < 0 };
}

// " · fragment «three words … three words»" for a version that voices only
// part of the text: both ends say what it is far better than the opening
// alone, which on two takes of the same sentence reads identically.
// The server sends the head and the tail of the spoken text separately (the
// middle of a long fragment never reaches here).
const bareWords = (s) => String(s || '')
  .replace(CHROME_RE, ' ').replace(/\s+/g, ' ').trim()
  .split(' ')
  .filter(Boolean);

function fragmentTag(v) {
  if (!v || !v.is_fragment) return '';
  const head = bareWords(v.fragment_text);
  const tailSrc = v.fragment_text_tail == null ? v.fragment_text : v.fragment_text_tail;
  const whole = String(v.fragment_text || '') === String(tailSrc || '');
  const tail = whole ? head : bareWords(tailSrc);
  if (!head.length) return ' · fragment';
  // Short enough to read in full — no point cutting six words down to six.
  if (whole && head.length <= 7) return ` · fragment «${head.join(' ')}»`;
  return ` · fragment «${head.slice(0, 3).join(' ')} … ${tail.slice(-3).join(' ')}»`;
}

// ----- Turns of a voiced text ---------------------------------------------------
// The "Speaker N:" turns as spans of the text: [{speaker, start, end, bodyStart,
// prefix}] — `start` is where the label begins, `bodyStart` where the words
// after the label and its [notes] begin, `end` where the next label starts.
// An empty list when the text has no labels. A turn that the re-voice recipes
// use as their unit: the whole stretch one voice reads in one go.
function textTurns(text) {
  const src = String(text || '');
  const marks = [];
  TURN_RE.lastIndex = 0;
  let m;
  while ((m = TURN_RE.exec(src)) !== null) marks.push(m);
  return marks.map((mk, i) => {
    const end = i + 1 < marks.length ? marks[i + 1].index : src.length;
    const label = mk[1].replace(/\s+/g, ' ');
    const tags = mk[2].trim().replace(/\s+/g, ' ');
    return {
      speaker: speakerOfPrefix(mk[1]),
      start: mk.index,
      bodyStart: mk.index + mk[0].length,
      end,
      prefix: `${label} ${tags ? `${tags} ` : ''}`,
    };
  });
}

// The turn (see textTurns) a character position falls in, or null.
function turnAt(text, pos) {
  return textTurns(text).find((t) => pos >= t.start && pos < t.end) || null;
}

module.exports = {
  CHROME_RE, TURN_RE,
  bareOf, wordFace, normWord, textWords, alignedForms, matchWords, mapRange,
  turnPrefix, speakerOfPrefix, withSpeaker, fragmentFromSelection, wordAtSelection,
  fragmentDonorMap, fragmentTag, textTurns, turnAt,
};
